/* FormsRenderer.js */

"use strict";
const _ = require("lodash");
const angular = require("angular");
const defaultLogger = require("./util/defaultLogger");
const glTextArea = require("@gluenetworks/gl_textarea/src/app/directives/gl_textarea.directive");
const glChosen = require("@gluenetworks/gl_chosen/src/app/directives/chosen.directive");
const glTextFieldDirective = require("@gluenetworks/gl_textfield/src/app/directives/gl_textfield.directive");
const glCheckboxDirective = require("@gluenetworks/gl-checkbox/src/app/directives/gl_checkbox.directive");
let glMultiSelectDirective = require("@gluenetworks/gl_multi_select/src/app/directives/gl_multi_select.directive.js");
const formRendererDirective = require("@gluenetworks/form_renderer/src/app/directives/gl_form_renderer.directive");

class FormsRenderer {
    constructor(model, logger = defaultLogger) {
        this._model = model;
        this._logger = logger;
        this._moduleId = Date.now().toString();
        this._rootElem = document.createElement("forms-gl");
        this._rootElem.component = this._model;

        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._model.on(
            "TypeInformationChanged",
            (this._onTypeInformationChanged = this._onTypeInformationChanged.bind(this))
        );
        this._model.on(
            "PristineValueChanged",
            (this._onPristineValueChanged = this._onPristineValueChanged.bind(this))
        );
        // Bind the function but don't register it until we have an angular scope.
        this._onRefLaunch = this._onRefLaunch.bind(this);
    }
    _unregisterListeners() {
        this._model.removeListener("TypeInformationChanged", this._onTypeInformationChanged);
        this._model.removeListener("PristineValueChanged", this._onPristineValueChanged);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    shutdown() {
        this._unregisterListeners();
        let promArr = [Promise.resolve()];

        return Promise.all(promArr).then(() => {
            this._model = undefined;
            if (this._formRendererApi) {
                this._formRendererApi.removeListener("ValueChanged", this._valueChangeListener);
                this._formRendererApi.removeListener("Invalid", this._invalidStatusChangeListener);
                this._formRendererApi.tearDown();
            }
            if (this._angularFormScope) {
                this._deregisterRefLaunchListener();
                this._angularFormScope.$destroy();
            }
            this._rootElem.remove();
            this._rootElem = undefined;
        });
    }
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return new Promise((resolve, reject) => {
            let that = this;
            if (!this._rootElem.hasChildNodes()) {
                //create the hidden module
                let angularModule = {};
                if (this._model._formsFromReact) {
                    glTextArea.$inject = ["$compile"];
                    let glTextAreaModule = angular
                        .module("gl_textarea", [])
                        .directive("glTextarea", glTextArea).name;

                    glTextFieldDirective.$inject = ["$compile", "$timeout"];
                    let glTextFieldModule = angular
                        .module("gl_textfield", [])
                        .directive("glTextfield", glTextFieldDirective).name;
                    glCheckboxDirective.$inject = ["$compile", "$timeout"];
                    let glCheckboxModule = angular
                        .module("gl_checkbox", [])
                        .directive("glCheckbox", glCheckboxDirective).name;
                    glChosen.$inject = ["$timeout"];
                    let glChosenModule = angular
                        .module("gl_chosen", [])
                        .directive("glChosen", glChosen).name;

                    glMultiSelectDirective.$inject = ["$compile", "$timeout"];
                    let glMultiSelectModule = angular
                        .module("gl_multi_select", [glChosenModule])
                        .directive("glMultiSelect", glMultiSelectDirective).name;

                    formRendererDirective.$inject = ["Logger", "$compile", "$injector", "$timeout"];
                    let formRendererModule = angular
                        .module("glFormRenderer", [
                            "templatecache",
                            glTextAreaModule,
                            glTextFieldModule,
                            glCheckboxModule,
                            glMultiSelectModule,
                        ])
                        .directive("glFormRenderer", formRendererDirective).name;
                    angularModule = angular.module(this._moduleId, [
                        "templatecache",
                        formRendererModule,
                    ]);
                } else {
                    angularModule = angular.module(this._moduleId, [
                        "templatecache",
                        "form_renderer",
                    ]);
                }
                angularModule.constant("Logger", this._logger);
                let angularInjector = angular.element(this._rootElem).injector();
                if (!angularInjector) {
                    //bootstrap the angular app in this custom element
                    angularInjector = angular.bootstrap(this._rootElem, [this._moduleId]);
                    this._bootStrappedAngular = true;
                }
                this._angularFormScope = angularInjector.get("$rootScope");

                if (!this._bootStrappedAngular) {
                    this._angularFormScope = this._angularFormScope.$new();
                }
                this._deregisterRefLaunchListener = this._angularFormScope.$on(
                    "RefLaunched",
                    this._onRefLaunch
                );

                //Compile the directive so Angular can do its thing
                angularInjector.invoke(function ($compile) {
                    let formRendererElem = document.createElement("gl-form-renderer");
                    formRendererElem.dataset.api = "FormRendererApi";
                    let linkFn = $compile(formRendererElem);
                    linkFn(that._angularFormScope, function (element) {
                        //Keep in mind element here is not the same as formRendererElem
                        that._rootElem.appendChild(element[0]);
                    });
                });

                //***NOTE*** Angular provides no way in knowing when a component is finished being compiled/linked this is the hacky way to do it...
                let $timeout = angularInjector.get("$timeout");
                $timeout(function () {
                    that._formRendererApi = that._angularFormScope.FormRendererApi;
                    that._renderForm().then(resolve, reject);
                });
            } else {
                this._renderForm().then(resolve, reject);
            }
        });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    isFormInvalid() {
        return this._formRendererApi ? this._formRendererApi.isInvalid() : false;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/
    _renderForm() {
        return Promise.resolve()
            .then(() => {
                return this._model.getAllPossibleRefOptions();
            })
            .then((refInstances) => {
                return new Promise((resolve) => {
                    let typeInfo = this._model.expandedPropNotation;
                    let value = this._model.getValue();
                    if (this._model._labFormattedRefs) {
                        this._model._mutateIntoControlFormat(value);
                    }
                    //TODO add better error handling

                    //TODO present loading Mask
                    //loaderMask.displayMask();

                    this._angularFormScope.$apply(() => {
                        this._formRendererApi.once("AllControlsLoaded", () => {
                            //loaderMask.hideMask();
                            setTimeout(() => {
                                this._formRendererApi.selectFirstElement();
                            }, 200);
                            resolve();
                        });
                        //Unregister/register value change listener
                        if (this._valueChangeListener) {
                            this._formRendererApi.removeListener(
                                "ValueChanged",
                                this._valueChangeListener
                            );
                            this._formRendererApi.removeListener(
                                "Invalid",
                                this._invalidStatusChangeListener
                            );
                        }
                        this._valueChangeListener = (eventDetails) => {
                            let newValue = _.get(eventDetails, "newValue", {});
                            let pendingPatches = this._model.getPatchesFromControlFormat(newValue);
                            this._model._setPendingPatches(pendingPatches);
                            //TODO the _formRendererApi is destructive meaning anything in the value that it doesn't understand it will throw away...
                            //this needs to be fixed but can't be addressed at this point
                        };
                        this._invalidStatusChangeListener = (eventDetails) => {
                            this._model._triggerInvalidStatusChangedEvent(eventDetails);
                        };
                        this._formRendererApi.on("ValueChanged", this._valueChangeListener);
                        this._formRendererApi.on("Invalid", this._invalidStatusChangeListener);

                        //Render the form and seed the configData if provided
                        this._formRendererApi.render(typeInfo, {
                            configurationData: value,
                            refInstances: refInstances,
                        });
                        if (this._formRendererApi.isInvalid()) {
                            this._invalidStatusChangeListener({
                                controlValueChangeEvent: null,
                                newValue: this._formRendererApi.isInvalid(),
                                oldValue: undefined,
                            });
                        }
                    });
                });
            });
    }
    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onTypeInformationChanged() {
        this._renderForm();
    }
    _onPristineValueChanged() {
        if (this._formRendererApi) {
            let pristineVal = this._model.getPristineValue();
            this._formRendererApi.setConfigurationData(pristineVal);
        }
    }
    _onRefLaunch(angularEvent, launchDeets) {
        this._model._refLaunched(launchDeets);
        if (this._angularFormScope.$root) {
            let cmeRouteData = {
                routeData: {
                    perspectiveFilter: {
                        construct: launchDeets.launchObj.construct,
                        type: launchDeets.launchObj.type,
                        selectedInstances: _.isEmpty(launchDeets.launchObj.instances)
                            ? []
                            : launchDeets.launchObj.instances,
                        filter: "",
                    },
                    zoomTo: [],
                },
            };
            this._angularFormScope.$root.$emit("ui.route.change", cmeRouteData);
        }
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = FormsRenderer;
